import React, {useState, useEffect, useRef} from "react";
import {
  Box,
  Button, CircularProgress,
  IconButton,
  Slider,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconEdit,
  IconTrash,
  IconEye,
  IconX,
  IconChecks,
  IconDeviceMobileMinus,
  IconDeviceMobileMessage,
  IconCircleCheck,
  IconCircle
} from "@tabler/icons-react";
import {TOKEN} from "../../../../context/SignInContext";
import {toast} from "react-toastify";
import useConfirm from "../../../../hooks/useConfirm";
import {DiffView} from "./DiffView";
import {MessageAnnotationsRepository} from "../../../../repositories/MessageAnnotationRepository";
import {ConversationPreviewModal} from "../../dashboard/components/ConversationPreviewModal";
import {useAtom} from "jotai";
import {SelectedConversationAtom} from "../../dashboard/atoms";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const AnnotationRow = ({
                                annotation,
                                selectedAudio,
                                setSelectedAudio,
                                configuration,
                                onUpdate,
                                onDelete,
                                viewMode, // Received from parent
                                project,
                              }) => {
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [conversationModalOpen, setConversationModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);

  const confirmDelete = useConfirm(
    'Delete Annotation',
    'Are you sure you want to delete this annotation? This action cannot be undone.'
  );

  const isEditing = editingId === annotation.id;
  const isDiffMode = viewMode === 'diff';

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedText("");
  };

  const handleEdit = () => {
    // Don't allow editing in diff mode
    if (isDiffMode) {
      toast.info("Please switch to Normal view to edit");
      return;
    }
    setEditingId(annotation.id);
    setEditedText(annotation.annotatedText || "");
  };

  // Cancel editing when switching to diff mode
  useEffect(() => {
    if (isDiffMode && isEditing) {
      handleCancelEdit();
    }
  }, [isDiffMode]);

  const handleSaveEdit = async () => {
    try {
      const response = await MessageAnnotationsRepository.createOrUpdate({
        ...annotation,
        annotatedText: editedText,
      });
      toast.success("Annotation updated successfully");
      setEditingId(null);
      setEditedText("");
      if (onUpdate && response.data) {
        onUpdate(response.data);
      }
    } catch (error) {
      console.error("Failed to update annotation:", error);
      toast.error("Failed to update annotation");
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDelete();
    if (confirmed) {
      try {
        await MessageAnnotationsRepository.deleteById(annotation.id);
        toast.success("Annotation deleted successfully");
        if (onDelete) onDelete();
      } catch (error) {
        console.error("Failed to delete annotation:", error);
        toast.error("Failed to delete annotation");
      }
    }
  };

  const handleViewConversation = (conversationId, messageId = undefined) => {
    setConversationModalOpen(true);
    if (conversationId) {
      setLoading(true)
      ConversationRepository.getConversation({id: conversationId})
        .then(response => {
          if (response?.data?.id === conversationId) {
            setSelectedConversation({...(response?.data || {}), messageId: messageId})
          }
        })
        .catch((error) => console.log(error))
        .finally(() => setLoading(false))
    }
  };

  const handleToggleResolved = async () => {
    try {
      const response = await MessageAnnotationsRepository.toggleResolved(annotation.id);
      toast.success(annotation.resolved ? "Annotation marked as unresolved" : "Annotation marked as resolved");
      if (onUpdate && response.data) {
        onUpdate(response.data);
      }
    } catch (error) {
      console.error("Failed to toggle resolved status:", error);
      toast.error("Failed to update annotation");
    }
  };

  return (
    <>
      {/*<ConversationPreviewModal*/}
      {/*  messageId={annotation.messageId}*/}
      {/*/>*/}
      <ConversationPreviewModal project={project}/>
      <TableRow
        hover
        sx={{
          bgcolor: annotation.resolved ? "grey.50" : "transparent",
        }}
      >
        <TableCell sx={{fontSize: 12, minWidth: 200, py: 1}}>
          {annotation?.originalText || "-"}
        </TableCell>
        <TableCell sx={{fontSize: 12, minWidth: 250, py: 1}}>
          <Stack direction="column" spacing={1}>
            {isEditing ? (
              <>
                <Stack direction="row" spacing={1}>
                  <TextField
                    multiline
                    fullWidth
                    sx={{fontSize: 12}}
                    size="small"
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                  />
                  <Box>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <IconX size={17}/>
                    </IconButton>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      variant="contained"
                      color={"success"}
                      onClick={handleSaveEdit}
                    >
                      <IconChecks size={17}/>
                    </IconButton>
                  </Box>
                </Stack>
              </>
            ) : isDiffMode ? (
              <DiffView
                originalText={annotation.originalText}
                annotatedText={annotation.annotatedText}
              />
            ) : (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{fontSize: 12, flex: 1}}>
                  {annotation.annotatedText || "-"}
                </Typography>
                <Tooltip title={isDiffMode ? "Switch to Normal view to edit" : "Edit annotation"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleEdit}
                      sx={{borderRadius: "50% !important"}}
                      disabled={isDiffMode}
                    >
                      <IconEdit size={18}/>
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </TableCell>
        <TableCell padding={"checkbox"} sx={{fontSize: 12, px: 2}}>
          <Typography fontSize={"inherit"}>
            {annotation?.cerScore?.toFixed(2) || "-"}
            &nbsp;/&nbsp;
            {annotation?.werScore?.toFixed(2) || "-"}
          </Typography>
        </TableCell>
        <TableCell padding={"checkbox"} sx={{fontSize: 12}}>
          {annotation.audio ? (
            <AudioPlayerCell
              audioPath={annotation.audio}
              annotationId={annotation.id}
              selectedAudio={selectedAudio}
              setSelectedAudio={setSelectedAudio}
              configuration={configuration}
            />
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell padding={"checkbox"} sx={{fontSize: 11}}>
          <Stack direction="row" spacing={1}>
            <Tooltip title={annotation.resolved ? "Mark as unresolved" : "Mark as resolved"}>
              <IconButton
                size="small"
                onClick={handleToggleResolved}
                sx={{borderRadius: "50% !important"}}
                color={annotation.resolved ? "success" : "default"}
              >
                {annotation.resolved ? <IconCircleCheck size={18}/> : <IconCircle size={18}/>}
              </IconButton>
            </Tooltip>
            {annotation.conversationId && (
              <Tooltip title="View conversation">
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => handleViewConversation(annotation.conversationId, annotation?.messageId)}
                  sx={{borderRadius: "50% !important"}}
                  color="secondary"
                >
                  {loading ? <CircularProgress color={"secondary"} size={16} /> : <IconDeviceMobileMessage size={18}/>}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete annotation">
              <IconButton
                size="small"
                disabled={loading}
                onClick={handleDelete}
                sx={{borderRadius: "50% !important"}}
                color="secondary"
              >
                <IconTrash size={18}/>
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * AudioPlayerCell component
 *
 * Props explanation:
 * - audioPath: The path to the audio file
 * - annotationId: The unique identifier for this annotation (annotation.id)
 * - selectedAudio: The annotationId of the currently playing audio (from parent state)
 * - setSelectedAudio: Function to update which audio is selected
 * - configuration: Project configuration containing the API base URL
 *
 * WHY WE USE annotationId:
 * The annotationId is used to implement a "single audio playback" feature.
 * When a user clicks play on one audio:
 * 1. We set selectedAudio to this annotation's annotationId
 * 2. All other AudioPlayerCell components receive this update via props
 * 3. Each component checks: if (selectedAudio !== annotationId) - pause my audio
 * 4. This ensures only ONE audio plays at a time across all table rows
 *
 * We use annotation.id (not messageId) because:
 * - It's the unique identifier for each row in the table
 * - It directly represents which annotation's audio is playing
 * - It's simpler and avoids potential conflicts
 */
const AudioPlayerCell = ({
                           audioPath,
                           annotationId,
                           selectedAudio,
                           setSelectedAudio,
                           configuration,
                         }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const getAudioUrl = (path) => {
    const base = configuration?.conoversation_controller_api || "https://api.voicebot.openbrain.io";
    const url = new URL("/api/file/audio", base);
    url.searchParams.append("path", path);
    url.searchParams.append("jwt", localStorage.getItem(TOKEN));
    return url.toString();
  };

  // Setup audio element when audioPath changes
  useEffect(() => {
    if (!audioPath) return;

    const url = getAudioUrl(audioPath);
    const audio = new Audio(url);
    audio.preload = "metadata";

    audioRef.current = audio;

    const handleMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.removeEventListener("loadedmetadata", handleMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioPath]);

  /**
   * This effect handles the "single audio playback" feature.
   * When selectedAudio changes (another audio starts playing),
   * this effect checks if this audio player should pause.
   */
  useEffect(() => {
    if (selectedAudio !== annotationId && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [selectedAudio, annotationId]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Set this audio as the selected one (pauses all others)
      setSelectedAudio(annotationId);
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(console.error);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{minWidth: 200}}>
      <IconButton size="small" onClick={togglePlay}>
        {isPlaying ? <IconPlayerPauseFilled size={14}/> : <IconPlayerPlayFilled size={14}/>}
      </IconButton>
      <Typography variant="body2" fontSize="0.95em" minWidth={30}>
        {formatTime(currentTime)}
      </Typography>
      <Slider
        size="small" color={"secondary"}
        min={0}
        max={duration || 0}
        step={0.1}
        value={currentTime}
        onChangeCommitted={(e, value) => handleSeek(value)}
        sx={{flexGrow: 1, maxWidth: 100}}
      />
      <Typography variant="body2" fontSize="0.95em" minWidth={30}>
        {formatTime(duration)}
      </Typography>
    </Stack>
  );
};